/**
 * @zendir/ui - Real-time Chart Streaming Hook
 *
 * Provides efficient real-time data streaming for charts.
 * Handles buffering, throttling, and memory management.
 */

import { useRef, useCallback, useEffect, useState } from "react";
import type { AstroChartHandle } from "./AstroChart";
import type { TimeSeriesPoint, DataPoint } from "./types";

// =============================================================================
// Types
// =============================================================================

export interface StreamOptions {
  /** Maximum data points to keep */
  maxPoints?: number;
  /** Time window in milliseconds (alternative to maxPoints) */
  timeWindow?: number;
  /** Throttle interval in milliseconds */
  throttleMs?: number;
  /** Buffer size before flushing */
  bufferSize?: number;
  /** Enable data decimation for large datasets */
  decimate?: boolean;
  /** Decimation threshold (start decimating above this count) */
  decimateThreshold?: number;
}

export interface StreamController {
  /** Push a single data point */
  push: (seriesIndex: number, point: TimeSeriesPoint | DataPoint) => void;
  /** Push multiple data points */
  pushBatch: (
    seriesIndex: number,
    points: (TimeSeriesPoint | DataPoint)[],
  ) => void;
  /** Clear all data */
  clear: () => void;
  /** Pause streaming */
  pause: () => void;
  /** Resume streaming */
  resume: () => void;
  /** Get current state */
  isPaused: boolean;
  /** Get current point count */
  pointCount: number;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useChartStream(
  chartRef: React.RefObject<AstroChartHandle>,
  options: StreamOptions = {},
): StreamController {
  const {
    maxPoints: _maxPoints = 1000,
    timeWindow: _timeWindow,
    throttleMs = 16, // ~60fps
    bufferSize = 10,
    decimate = true,
    decimateThreshold = 5000,
  } = options;

  const bufferRef = useRef<Map<number, (TimeSeriesPoint | DataPoint)[]>>(
    new Map(),
  );
  const lastFlushRef = useRef<number>(0);
  const pointCountRef = useRef<number>(0);
  const [isPaused, setIsPaused] = useState(false);
  const animationFrameRef = useRef<number>();

  // Flush buffer to chart
  const flush = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || isPaused) return;

    const now = Date.now();
    if (now - lastFlushRef.current < throttleMs) {
      // Schedule next flush
      animationFrameRef.current = requestAnimationFrame(flush);
      return;
    }

    bufferRef.current.forEach((points, seriesIndex) => {
      if (points.length === 0) return;

      // Apply decimation if needed
      let processedPoints = points;
      if (decimate && pointCountRef.current > decimateThreshold) {
        processedPoints = decimateData(points, Math.ceil(points.length / 2));
      }

      // Append each point
      processedPoints.forEach((point) => {
        chart.appendData(seriesIndex, point);
      });

      pointCountRef.current += processedPoints.length;
    });

    // Clear buffer
    bufferRef.current.clear();
    lastFlushRef.current = now;
  }, [chartRef, isPaused, throttleMs, decimate, decimateThreshold]);

  // Push single point
  const push = useCallback(
    (seriesIndex: number, point: TimeSeriesPoint | DataPoint) => {
      if (isPaused) return;

      let buffer = bufferRef.current.get(seriesIndex);
      if (!buffer) {
        buffer = [];
        bufferRef.current.set(seriesIndex, buffer);
      }

      buffer.push(point);

      // Flush if buffer is full
      if (buffer.length >= bufferSize) {
        flush();
      } else if (!animationFrameRef.current) {
        // Schedule flush
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = undefined;
          flush();
        });
      }
    },
    [isPaused, bufferSize, flush],
  );

  // Push batch
  const pushBatch = useCallback(
    (seriesIndex: number, points: (TimeSeriesPoint | DataPoint)[]) => {
      if (isPaused || points.length === 0) return;

      let buffer = bufferRef.current.get(seriesIndex);
      if (!buffer) {
        buffer = [];
        bufferRef.current.set(seriesIndex, buffer);
      }

      buffer.push(...points);
      flush();
    },
    [isPaused, flush],
  );

  // Clear all data
  const clear = useCallback(() => {
    bufferRef.current.clear();
    pointCountRef.current = 0;
    chartRef.current?.clearData();
  }, [chartRef]);

  // Pause/resume
  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return {
    push,
    pushBatch,
    clear,
    pause,
    resume,
    isPaused,
    pointCount: pointCountRef.current,
  };
}

// =============================================================================
// Data Decimation
// =============================================================================

/**
 * Largest Triangle Three Buckets (LTTB) algorithm for data decimation
 * Preserves visual shape while reducing point count
 */
function decimateData<T extends TimeSeriesPoint | DataPoint>(
  data: T[],
  threshold: number,
): T[] {
  if (data.length <= threshold) return data;

  const result: T[] = [];
  const bucketSize = (data.length - 2) / (threshold - 2);

  // Always include first point
  result.push(data[0]);

  let a = 0; // Previous selected point

  for (let i = 0; i < threshold - 2; i++) {
    // Calculate bucket range
    const bucketStart = Math.floor((i + 1) * bucketSize) + 1;
    const bucketEnd = Math.floor((i + 2) * bucketSize) + 1;
    const bucketLength = Math.min(bucketEnd, data.length) - bucketStart;

    // Calculate average point in next bucket
    let avgX = 0;
    let avgY = 0;
    const nextBucketStart = Math.floor((i + 2) * bucketSize) + 1;
    const nextBucketEnd = Math.min(
      Math.floor((i + 3) * bucketSize) + 1,
      data.length,
    );
    const nextBucketLength = nextBucketEnd - nextBucketStart;

    for (let j = nextBucketStart; j < nextBucketEnd; j++) {
      const point = data[j];
      const [x, y] = getPointXY(point);
      avgX += x;
      avgY += y;
    }
    avgX /= nextBucketLength || 1;
    avgY /= nextBucketLength || 1;

    // Find point with largest triangle area
    const [aX, aY] = getPointXY(data[a]);
    let maxArea = -1;
    let maxIdx = bucketStart;

    for (let j = bucketStart; j < bucketStart + bucketLength; j++) {
      const point = data[j];
      const [x, y] = getPointXY(point);

      // Triangle area
      const area = Math.abs((aX - avgX) * (y - aY) - (aX - x) * (avgY - aY));

      if (area > maxArea) {
        maxArea = area;
        maxIdx = j;
      }
    }

    result.push(data[maxIdx]);
    a = maxIdx;
  }

  // Always include last point
  result.push(data[data.length - 1]);

  return result;
}

function getPointXY(point: TimeSeriesPoint | DataPoint): [number, number] {
  if ("time" in point) {
    return [
      typeof point.time === "number"
        ? point.time
        : new Date(point.time).getTime(),
      point.value,
    ];
  }
  return [typeof point.x === "number" ? point.x : 0, point.y];
}

// =============================================================================
// WebSocket Stream Hook
// =============================================================================

export interface WebSocketStreamOptions extends StreamOptions {
  /** WebSocket URL */
  url: string;
  /** Reconnect on disconnect */
  reconnect?: boolean;
  /** Reconnect delay in milliseconds */
  reconnectDelay?: number;
  /** Maximum reconnect attempts */
  maxReconnects?: number;
  /** Message parser */
  parseMessage?: (
    data: unknown,
  ) => { seriesIndex: number; point: TimeSeriesPoint | DataPoint } | null;
}

export interface WebSocketStreamController extends StreamController {
  /** WebSocket connection state */
  connectionState: "connecting" | "connected" | "disconnected" | "error";
  /** Manually connect */
  connect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

export function useWebSocketStream(
  chartRef: React.RefObject<AstroChartHandle>,
  options: WebSocketStreamOptions,
): WebSocketStreamController {
  const {
    url,
    reconnect = true,
    reconnectDelay = 1000,
    maxReconnects = 10,
    parseMessage,
    ...streamOptions
  } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [connectionState, setConnectionState] =
    useState<WebSocketStreamController["connectionState"]>("disconnected");

  const stream = useChartStream(chartRef, streamOptions);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setConnectionState("connecting");
    const ws = new WebSocket(url);

    ws.onopen = () => {
      setConnectionState("connected");
      reconnectCountRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const parsed = parseMessage
          ? parseMessage(data)
          : defaultParseMessage(data);

        if (parsed) {
          stream.push(parsed.seriesIndex, parsed.point);
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.error("Failed to parse WebSocket message:", e);
        }
      }
    };

    ws.onerror = () => {
      setConnectionState("error");
    };

    ws.onclose = () => {
      setConnectionState("disconnected");

      if (reconnect && reconnectCountRef.current < maxReconnects) {
        reconnectCountRef.current++;
        reconnectTimerRef.current = setTimeout(
          connect,
          reconnectDelay * reconnectCountRef.current,
        );
      }
    };

    wsRef.current = ws;
  }, [url, reconnect, reconnectDelay, maxReconnects, parseMessage, stream]);

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimerRef.current);
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  // Auto-connect on mount
  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return {
    ...stream,
    connectionState,
    connect,
    disconnect,
  };
}

function defaultParseMessage(
  data: unknown,
): { seriesIndex: number; point: TimeSeriesPoint } | null {
  if (typeof data !== "object" || data === null) return null;

  const obj = data as Record<string, unknown>;

  if ("time" in obj && "value" in obj) {
    return {
      seriesIndex: typeof obj.seriesIndex === "number" ? obj.seriesIndex : 0,
      point: {
        time: obj.time as number,
        value: obj.value as number,
      },
    };
  }

  return null;
}
